import dayjs, { ManipulateType, OpUnitType } from 'dayjs'

/**
 * Adds a date unit to a date value.
 * @param date Base date (`Date | string | null`).
 * @param amount Amount to add (absolute value is used).
 * @param unit Dayjs manipulation unit (defaults to `days`).
 * @param utc Whether to operate in UTC mode (defaults to `false`).
 * @returns Updated `Date`, or `null` when `date` is invalid.
 */
export const addDateUnit = (
  date: Date | string | null,
  amount: number,
  unit: ManipulateType = 'days',
  utc: boolean = false,
): Date | null => {
  const d = utc ? dayjs.utc(date) : dayjs(date)
  return d.isValid() ? d.add(Math.abs(amount), unit).toDate() : null
}

/**
 * Subtracts a date unit from a date value.
 * @param date Base date (`Date | string | null`).
 * @param amount Amount to subtract (absolute value is used).
 * @param unit Dayjs manipulation unit (defaults to `days`).
 * @param utc Whether to operate in UTC mode (defaults to `false`).
 * @returns Updated `Date`, or `null` when `date` is invalid.
 */
export const subtractDateUnit = (
  date: Date | string | null,
  amount: number,
  unit: ManipulateType = 'days',
  utc: boolean = false,
): Date | null => {
  const d = utc ? dayjs.utc(date) : dayjs(date)
  return d.isValid() ? d.subtract(Math.abs(amount), unit).toDate() : null
}

/**
 * Returns the start of a date for a specific unit.
 * @param date Input date (`Date | string | null`).
 * @param unit Unit boundary to use (defaults to `day`).
 * @param utc Whether to operate in UTC mode (defaults to `false`).
 * @returns `Date` at start of unit, or `null` when `date` is invalid.
 */
export const startOfDate = (
  date: Date | string | null,
  unit: OpUnitType = 'day',
  utc: boolean = false,
): Date | null => {
  const d = utc ? dayjs.utc(date) : dayjs(date)
  return d.isValid() ? d.startOf(unit).toDate() : null
}

/**
 * Returns the end of a date for a specific unit.
 * @param date Input date (`Date | string | null`).
 * @param unit Unit boundary to use (defaults to `day`).
 * @param utc Whether to operate in UTC mode (defaults to `false`).
 * @returns `Date` at end of unit, or `null` when `date` is invalid.
 */
export const endOfDate = (
  date: Date | string | null,
  unit: OpUnitType = 'day',
  utc: boolean = false,
): Date | null => {
  const d = utc ? dayjs.utc(date) : dayjs(date)
  return d.isValid() ? d.endOf(unit).toDate() : null
}

/**
 * Resets a date value to the start of its day.
 * @param date Input date (`Date | string | null`).
 * @param utc Whether to operate in UTC mode (defaults to `false`).
 * @returns `Date` at 00:00:00.000, or `null` when `date` is invalid.
 */
export const wipeDateHours = (
  date: Date | string | null,
  utc: boolean = false,
): Date | null => {
  const d = utc ? dayjs.utc(date) : dayjs(date)
  return d.isValid() ? d.startOf('day').toDate() : null
}
